import React, { useState } from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { Heading as HeadingBase } from 'js/design_system/Heading';
import { Button } from 'js/social/shared/design_system/Button';
import CheckIcon from 'js/design_system/icons/check.svg';
import { FollowableUser } from 'js/social/shared/types';
import { OverlappingAvatars } from 'js/social/shared/OverlappingAvatars';
import { createUserBulkFollowing } from 'js/social/api/user_bulk_following_api';

const Container = styled.div<{ compact?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 40px;
  color: $ds-black;

  &.compact {
    align-items: flex-start;
    text-align: left;
    gap: 12px;
  }
`;

const ListPreviewContainer = styled.div<{ compact?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 0px 32px;
  justify-content: center;
  align-items: center;
  gap: 8px;

  &.compact {
    flex-direction: row;
    padding: 0px;
  }
`;

const CtaContainer = styled.div`
  display: flex;
  gap: 12px;
  align-self: stretch;
  justify-content: center;
`;

const Heading = styled(HeadingBase)`
  margin-top: 16px;
`;

const Description = styled(Body)<{ compact?: boolean }>`
  &:not(.compact) {
    max-width: 256px;
  }
`;

const Cta = styled(Button)<{ compact?: boolean }>`
  width: 100%;

  &:not(.compact) {
    @include for-tablet-up {
      margin-bottom: 20px;
      max-width: 394px;
    }
  }
`;

const CtaContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export type Props = {
  topic: {
    name: string;
    score: number;
  };
  header: JSX.Element | null;
  description: JSX.Element | null;
  users: FollowableUser[];
  followingSource: string;
  variant?: 'full' | 'compact';
  previewCount?: number;
  onComplete: () => void;
  onViewAll?: () => void;
};

/**
 * The layout of bulk follow preview modals.
 * @param {JSX.Element | null} header Modal header
 * @param {JSX.Element | null} description Modal description
 * @param {object} topic RI topic used for the bulk follow list
 * @param {FollowableUser[]} users Users featured in the bulk follow list
 * @param {string} followingSource Following source to use for the "follow all" action
 * @param {('full' | 'compact')} [variant='full'] The layout variant to use
 *   - 'full': Used on large surfaces such as takeover modals or big cards (default)
 *   - 'compact': Used for smaller surfaces such as the sidebar
 * @param {number} [previewCount=variant === 'compact' ? 3 : 6] Number of users to preview in the list
 * @param {() => void} onComplete Callback to run after the "follow all" action is complete
 * @param {() => void} onViewAll If present, include a "view all" CTA with this callback
 */
export const BulkFollowPreview: React.FC<Props> = ({
  topic,
  header,
  description,
  users,
  followingSource,
  variant = 'full',
  previewCount = variant === 'compact' ? 3 : 6,
  onComplete,
  onViewAll,
}) => {
  const [state, setState] = useState<'start' | 'loading' | 'complete'>('start');
  const compact = variant === 'compact';

  const followAll = async () => {
    if (state !== 'start') return;

    setState('loading');

    const followDetailMap: {
      [key: number]: { [key: string]: string | number };
    } = {};

    users.forEach((user) => {
      followDetailMap[user.id] = {
        topic: topic.name,
        topic_score: topic.score,
        individual_follow: 'false',
      };
    });
    await createUserBulkFollowing(
      users.map((user) => user.id),
      followingSource,
      false,
      followDetailMap,
    );
    setState('complete');
    setTimeout(onComplete, 1000);
  };

  return (
    <Container compact={compact}>
      {compact ? <Body>{header}</Body> : <Heading size="sm">{header}</Heading>}

      <ListPreviewContainer compact={compact}>
        <OverlappingAvatars
          disableLinks
          size={40}
          users={users.slice(0, previewCount)}
        />
        <Description compact={compact} size="sm">
          {description}
        </Description>
      </ListPreviewContainer>

      <CtaContainer>
        <Cta
          compact={compact}
          size={compact ? 'sm' : 'lg'}
          variant={compact ? 'inverse' : 'primary'}
          disabled={state === 'loading'}
          onClick={followAll}
        >
          {state === 'complete' ? (
            <CtaContent>
              <CheckIcon /> Following all
            </CtaContent>
          ) : (
            'Follow all'
          )}
        </Cta>
        {onViewAll && (
          <Cta
            compact={compact}
            size={compact ? 'sm' : 'lg'}
            variant={compact ? 'inverse' : 'primary'}
            disabled={state === 'loading'}
            onClick={onViewAll}
          >
            View all
          </Cta>
        )}
      </CtaContainer>
    </Container>
  );
};
